/**
 * useRoutingRuleCategories - Hook de categorias para Ações Rápidas (Macros)
 *
 * Fornece query (listar categorias visíveis ao usuário) e mutations
 * (criar/atualizar/excluir) usando exclusivamente as RPCs:
 *   - get_routing_rule_categories
 *   - upsert_routing_rule_category
 *   - delete_routing_rule_category
 *
 * Visibilidade: private (apenas dono), shared (lista de usuários) e
 * public (todos do tenant). Espelha o modelo das próprias regras
 * manuais (routing_rules), respeitando o padrão de segurança
 * tenant v2.1 do projeto.
 *
 * CHANGELOG:
 *   2026-06-23 v1.0.0 - Versão inicial.
 *
 * @version 1.0.0
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { showToast } from '@/lib/toast';
import { createLogger } from '@/lib/logger';

// Logger dedicado do hook
const log = createLogger('useRoutingRuleCategories');

// Visibilidade da categoria (mesmas opções da regra)
export type RoutingCategoryVisibility = 'private' | 'shared' | 'public';

// Modelo retornado pela RPC get_routing_rule_categories
export interface RoutingRuleCategory {
  id: string;
  name: string;
  description: string | null;
  color: string;
  icon: string;
  sort_order: number;
  visibility: RoutingCategoryVisibility;
  created_by: string | null;
  is_owner: boolean;
  rules_count: number;
  created_at: string;
  updated_at: string;
}

// Payload para criar/atualizar categoria
export interface UpsertRoutingCategoryInput {
  /** ID quando edição; omitido na criação */
  id?: string;
  name: string;
  description?: string | null;
  color?: string;
  icon?: string;
  sort_order?: number;
  visibility: RoutingCategoryVisibility;
  /** Usuários liberados quando visibility = 'shared' */
  allowed_user_ids?: string[];
}

/**
 * Hook principal: lista categorias visíveis ao usuário + mutations.
 */
export function useRoutingRuleCategories() {
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();

  // Chave única; permite invalidar por toda a feature
  const queryKey = ['routing-rule-categories', currentTenant?.id] as const;

  const listQuery = useQuery({
    queryKey,
    queryFn: async () => {
      if (!currentTenant?.id) return [] as RoutingRuleCategory[];

      log.debug('Buscando categorias de ações rápidas', { tenantId: currentTenant.id });
      const { data, error } = await supabase.rpc('get_routing_rule_categories', {
        p_tenant_hint: currentTenant.id,
      });
      if (error) {
        log.error('Erro ao listar categorias', { error: error.message });
        throw error;
      }
      return (data || []) as RoutingRuleCategory[];
    },
    enabled: !!currentTenant?.id,
    staleTime: 30000,
  });

  // Cria ou atualiza uma categoria
  const upsertMutation = useMutation({
    mutationFn: async (input: UpsertRoutingCategoryInput) => {
      if (!currentTenant?.id) {
        throw new Error('Tenant não selecionado');
      }
      const { data, error } = await supabase.rpc('upsert_routing_rule_category', {
        p_name: input.name,
        p_description: input.description ?? null,
        p_color: input.color ?? '#f59e0b',
        p_icon: input.icon ?? 'Zap',
        p_sort_order: input.sort_order ?? 0,
        p_visibility: input.visibility,
        p_allowed_user_ids:
          input.visibility === 'shared' && input.allowed_user_ids?.length
            ? input.allowed_user_ids
            : null,
        p_id: input.id ?? null,
        p_tenant_hint: currentTenant.id,
      });
      if (error) {
        log.error('Erro ao salvar categoria', { error: error.message });
        throw error;
      }
      return data as string;
    },
    onSuccess: (_id, vars) => {
      queryClient.invalidateQueries({ queryKey: ['routing-rule-categories'] });
      // Invalida também as listagens de ações rápidas (Sheet/Popover)
      queryClient.invalidateQueries({ queryKey: ['quick-action-rules'] });
      showToast.success(vars.id ? 'Categoria atualizada' : 'Categoria criada');
    },
    onError: (err: Error) => {
      showToast.error('Erro ao salvar categoria', err.message);
    },
  });

  // Remove uma categoria (regras vinculadas ficam sem categoria)
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      if (!currentTenant?.id) throw new Error('Tenant não selecionado');
      const { error } = await supabase.rpc('delete_routing_rule_category', {
        p_id: id,
        p_tenant_hint: currentTenant.id,
      });
      if (error) {
        log.error('Erro ao excluir categoria', { error: error.message });
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['routing-rule-categories'] });
      queryClient.invalidateQueries({ queryKey: ['quick-action-rules'] });
      showToast.success('Categoria excluída');
    },
    onError: (err: Error) => {
      showToast.error('Erro ao excluir categoria', err.message);
    },
  });

  return {
    categories: listQuery.data ?? [],
    isLoading: listQuery.isLoading,
    error: listQuery.error,
    refetch: listQuery.refetch,
    upsert: upsertMutation.mutateAsync,
    isUpserting: upsertMutation.isPending,
    remove: deleteMutation.mutateAsync,
    isRemoving: deleteMutation.isPending,
  };
}
